// Third-party
import React from 'react';
import classNames from 'classnames';
import { Dropdown } from 'react-bootstrap';

// Components
import { MenuToggle } from '../../../components/buttons';

// Utils
import { formatMessage, FormattedMessage } from '../../../../utils/i18n';
import { getHoldingsSymbols, isBenchmark, isMissingBenchmark, shouldExpandPortfolioInReadOnly } from '../../../../utils/comparison';
import { PORTFOLIO_TYPES } from '../../../../utils/portfolios';
import { COMPARISON_ITEM_TYPES, YieldParamsType } from '../../../../utils/constants';
import RateLimitedComponent from '../../../components/RateLimitedComponent';
import { RateLimitType } from '../../../../redux/modules/types/rate-limits';
import { ErrorTooltip } from '../../../components/tooltips';
import { PERMISSIONS, userHasPermission } from '../../../../utils/permissions';
import ComparisonItem from '@/types/comparison-item';
import User from '@/types/user';

type Props = {
  item: ComparisonItem;
  disabled?: boolean;
  canEdit: boolean;
  readOnly?: boolean;
  user?: User | null;
  onItemRemove: () => void;
  onModifyPortfolio: () => void;
  onViewPortfolio: () => void;
  onExpandPortfolio: (expand: boolean) => void;
  onClonePortfolio: () => void;
  onAddCategoryBenchmark?: () => void;
  onCloneBenchmark: () => void;
  markupMode?: boolean;
  incomeStream?: boolean;
  showPortfolioSaveModal?: boolean;
  onModifyBenchmark: () => void;
  onSavePortfolio: (toContact?: boolean) => void;
  disableSaving?: boolean;
  scrollbarContainerRef?: HTMLElement;
  hasStickyMenu?: boolean;
  onModifyGroup: () => void;
  canEditGroup?: boolean;
};

type MenuItem = {
  value: React.ReactElement | string;
  className?: string;
  onClick: () => void;
  hidden?: boolean;
  icon?: string;
  disabled?: boolean;
};

class LegacyComparisonCardMenu extends React.Component<Props> {
  static defaultProps = {
    canEdit: true,
    onItemRemove: () => {},
    onModifyPortfolio: () => {},
    onViewPortfolio: () => {},
    onExpandPortfolio: () => {},
    onClonePortfolio: () => {},
    onCloneBenchmark: () => {},
    onModifyBenchmark: () => {},
    onModifyGroup: () => {},
  };

  removeItem = () => {
    this.props.onItemRemove();
  };

  editPortfolio = () => {
    this.props.onModifyPortfolio();
  };

  savePortfolioToContact = () => {
    this.props.onSavePortfolio(true);
  };

  savePortfolioAsModel = () => {
    this.props.onSavePortfolio();
  };

  clonePortfolio = () => {
    this.props.onClonePortfolio();
  };

  addCategoryBenchmark = () => {
    if (this.props.onAddCategoryBenchmark) {
      this.props.onAddCategoryBenchmark();
    }
  };

  cloneBenchmark = () => {
    this.props.onCloneBenchmark();
  };

  expandPortfolio = (expand: boolean) => {
    this.props.onExpandPortfolio(expand);
  };

  editBenchmark = () => {
    this.props.onModifyBenchmark();
  };

  modifyGroup = () => {
    this.props.onModifyGroup();
  };

  viewPortfolio = () => {
    this.props.onViewPortfolio();
  };

  getMenuItems = () => {
    const { user, item, readOnly, canEdit, markupMode, incomeStream, disableSaving, canEditGroup } = this.props;
    const isPortfolio = item.type === COMPARISON_ITEM_TYPES.PORTFOLIO;
    const portfolioIsUnassigned = item.portfolioType === PORTFOLIO_TYPES.UNASSIGNED;
    const portfolioHasGicOrHisa =
      isPortfolio && getHoldingsSymbols(item.holdings).some(symbol => symbol === YieldParamsType.GIC || symbol === YieldParamsType.HISA);

    const localCanEdit = canEdit && (item.availableActions == null || item.availableActions.edit) && !item.overrideHoldings;
    const canClone = item.availableActions == null || item.availableActions.clone;

    const hideExpandPortfolio = !isPortfolio || readOnly || markupMode || incomeStream;

    const hideSaveAsContact =
      disableSaving ||
      readOnly ||
      !localCanEdit ||
      !!item.enterprise ||
      !isPortfolio ||
      markupMode ||
      !portfolioIsUnassigned ||
      !userHasPermission(user, PERMISSIONS.PORTFOLIOS_SAVE_TO_CONTACT);

    const hideSaveAsModel =
      disableSaving ||
      readOnly ||
      !localCanEdit ||
      !!item.enterprise ||
      !isPortfolio ||
      markupMode ||
      !portfolioIsUnassigned ||
      !userHasPermission(user, PERMISSIONS.USER_MODELS_CREATE);

    const hideModifyPortfolio = readOnly || !localCanEdit || !!item.enterprise || !isPortfolio || markupMode;

    const hideEditBenchmark = readOnly || !isBenchmark(item) || markupMode || incomeStream;

    const hideClonePortfolio = readOnly || !isPortfolio || markupMode || !canClone;

    const hideAddCategoryBenchmark =
      readOnly ||
      incomeStream ||
      markupMode ||
      !userHasPermission(user, PERMISSIONS.BENCHMARKS) ||
      isBenchmark(item) ||
      isMissingBenchmark(item) ||
      item.type === COMPARISON_ITEM_TYPES.GROUP ||
      !this.props.onAddCategoryBenchmark ||
      portfolioHasGicOrHisa;

    const hideCloneBenchmark = readOnly || !isBenchmark(item) || item.portfolioType !== PORTFOLIO_TYPES.CUSTOM || markupMode || incomeStream;

    const hideModifyGroup = readOnly || !canEditGroup || markupMode || incomeStream || item.type !== COMPARISON_ITEM_TYPES.GROUP;

    const isPortfolioExpandLimited = shouldExpandPortfolioInReadOnly(item, user);

    const hideViewPortfolio = (!isPortfolio || markupMode || (canEdit && !readOnly && !item.enterprise)) && !isPortfolioExpandLimited;

    const hideEditLimited =
      !isPortfolio || markupMode || !(canEdit && !readOnly && !item.enterprise) || localCanEdit || (item.overrideHoldings && !item.overrideHoldings.length);

    const menuItems: MenuItem[] = [
      {
        value: (
          <RateLimitedComponent type={RateLimitType.MODELS}>
            {({ disabled, error }) => (
              <Dropdown.Item
                role="menuitem"
                as="button"
                onClick={disabled ? undefined : this.savePortfolioAsModel}
                className={classNames({ 'bottom-border': hideSaveAsContact, disabled })}
              >
                <i className="icon-save" />
                <FormattedMessage id="Comparison.ItemMenu.SaveAsModel" />
                {error && <ErrorTooltip message={error} />}
              </Dropdown.Item>
            )}
          </RateLimitedComponent>
        ),
        onClick: () => {},
        hidden: hideSaveAsModel,
      },
      {
        value: formatMessage('Comparison.ItemMenu.SaveToContact'),
        className: classNames({ 'bottom-border': !hideSaveAsContact }),
        onClick: this.savePortfolioToContact,
        icon: 'save-contact',
        hidden: hideSaveAsContact,
      },
      {
        value: formatMessage('Comparison.ItemMenu.ModifyPortfolio'),
        onClick: this.editPortfolio,
        icon: 'edit',
        hidden: hideModifyPortfolio,
      },
      {
        value: formatMessage('Comparison.ItemMenu.EditPortfolioLimited'),
        onClick: this.viewPortfolio,
        icon: 'edit',
        hidden: hideEditLimited,
      },
      {
        value: formatMessage('Comparison.ItemMenu.ViewPortfolio'),
        onClick: this.viewPortfolio,
        icon: 'view',
        hidden: hideViewPortfolio,
      },
      {
        value: formatMessage(`Comparison.ItemMenu.Benchmark.${item.holdings.length > 1 ? 'Modify' : 'CreateCustom'}`),
        onClick: this.editBenchmark,
        icon: 'sliders',
        hidden: hideEditBenchmark,
      },
      {
        value: formatMessage(`Comparison.ItemMenu.${item.isExpanded ? 'CollapsePortfolio' : 'ExpandPortfolio'}`),
        onClick: () => this.expandPortfolio(!item.isExpanded),
        icon: 'expand',
        hidden: hideExpandPortfolio,
        disabled: isPortfolioExpandLimited,
      },
      {
        value: formatMessage('Comparison.ItemMenu.ClonePortfolio'),
        onClick: this.clonePortfolio,
        icon: 'clone',
        hidden: hideClonePortfolio,
      },
      {
        value: formatMessage('Comparison.ItemMenu.AddCategoryBenchmark'),
        onClick: this.addCategoryBenchmark,
        icon: 'add-category-benchmark',
        hidden: hideAddCategoryBenchmark,
      },
      {
        value: formatMessage('Comparison.ItemMenu.CloneBenchmark'),
        onClick: this.cloneBenchmark,
        icon: 'clone',
        hidden: hideCloneBenchmark,
      },
      {
        value: formatMessage('Comparison.ItemMenu.EditGroup'),
        onClick: this.modifyGroup,
        icon: 'edit',
        hidden: hideModifyGroup,
      },
    ].filter(i => !i.hidden);

    if (!readOnly) {
      const hasPriorItems = menuItems.length > 0;
      menuItems.push({
        value: formatMessage('Comparison.ItemMenu.Remove'),
        className: classNames('destructive', hasPriorItems ? 'top-border' : undefined),
        icon: 'remove',
        onClick: this.removeItem,
      });
    }

    return menuItems;
  };

  renderOptions = () =>
    this.getMenuItems().map((item, key) => {
      if (typeof item.value === 'string') {
        return (
          <Dropdown.Item key={key} role="menuitem" as="button" className={classNames(item.className)} onClick={() => item.onClick()} disabled={item.disabled}>
            <i className={`icon-${item.icon}`} />
            {item.value}
          </Dropdown.Item>
        );
      }

      return item.value;
    });

  render() {
    const { disabled, hasStickyMenu, scrollbarContainerRef } = this.props;
    const options = this.renderOptions();

    if (options.length === 0) {
      return null;
    }

    return (
      <div className="comparison-card-menu" onClick={event => event.stopPropagation()}>
        <MenuToggle disabled={disabled} options={options} scrollbarContainerRef={scrollbarContainerRef} hideOnScrollAway={!hasStickyMenu} alignRight />
      </div>
    );
  }
}

export default LegacyComparisonCardMenu;
